import React, { useEffect, useMemo } from 'react';
import { Authenticator, ThemeProvider } from '@aws-amplify/ui-react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';

export default function LoginModal() {
  const { showLoginModal, closeLoginModal, onLoginSuccess, loginMessage } = useAuth();
  const brand = useBrand();

  // Dynamic theme based on brand config
  const theme = useMemo(() => ({
    name: `${brand.name.toLowerCase()}-theme`,
    tokens: {
      colors: {
        brand: {
          primary: {
            10: { value: brand.colors.amplifyTheme['10'] },
            20: { value: brand.colors.amplifyTheme['20'] },
            40: { value: brand.colors.amplifyTheme['40'] },
            60: { value: brand.colors.amplifyTheme['60'] },
            80: { value: brand.colors.amplifyTheme['80'] },
            90: { value: brand.colors.amplifyTheme['90'] },
            100: { value: brand.colors.amplifyTheme['100'] },
          },
        },
      },
      fonts: {
        default: {
          variable: { value: `${brand.fonts.primary}, system-ui, sans-serif` },
          static: { value: `${brand.fonts.primary}, system-ui, sans-serif` },
        },
      },
    },
  }), [brand]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showLoginModal) {
        closeLoginModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showLoginModal, closeLoginModal]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showLoginModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showLoginModal]);

  if (!showLoginModal) return null;

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeLoginModal();
      }}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-[420px] relative shadow-2xl overflow-hidden"
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="relative w-9 h-9">
              <div
                className="absolute inset-0 rounded-xl rotate-3 shadow-lg"
                style={{
                  background: `linear-gradient(to bottom right, ${brand.colors.primary}, ${brand.colors.secondary})`,
                  boxShadow: `0 10px 15px -3px ${brand.colors.primary}40`
                }}
              ></div>
              <div
                className="absolute inset-0 rounded-xl flex items-center justify-center"
                style={{
                  background: `linear-gradient(to bottom right, ${brand.colors.primaryHover}, ${brand.colors.secondary})`
                }}
              >
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold text-slate-900">
                {brand.nameParts.main}<span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(to right, ${brand.colors.primary}, ${brand.colors.secondary})`
                  }}
                >{brand.nameParts.accent}</span>
              </span>
              <span className="text-[10px] font-medium text-slate-400 -mt-0.5">{brand.tagline}</span>
            </div>
          </div>
          <button
            onClick={closeLoginModal}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Message */}
        {loginMessage && (
          <div className="px-6 pt-5 pb-2">
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-center">
              <p className="text-sm text-blue-700 font-medium">{loginMessage}</p>
            </div>
          </div>
        )}

        {/* Authenticator */}
        <div className="px-6 py-4 auth-container">
          <ThemeProvider theme={theme}>
            <Authenticator
              socialProviders={[]}
              signUpAttributes={['email']}
              formFields={{
                signIn: {
                  username: {
                    placeholder: 'Enter your email',
                    label: 'Email',
                    labelHidden: false,
                  },
                  password: {
                    placeholder: 'Enter your password',
                    label: 'Password',
                    labelHidden: false,
                  },
                },
                signUp: {
                  email: {
                    placeholder: 'Enter your email',
                    label: 'Email',
                    labelHidden: false,
                    order: 1,
                  },
                  password: {
                    placeholder: 'Create a password',
                    label: 'Password',
                    labelHidden: false,
                    order: 2,
                  },
                  confirm_password: {
                    placeholder: 'Confirm your password',
                    label: 'Confirm Password',
                    labelHidden: false,
                    order: 3,
                  },
                },
              }}
            >
              {({ user }) => {
                if (user) {
                  setTimeout(() => onLoginSuccess(), 0);
                }
                return null;
              }}
            </Authenticator>
          </ThemeProvider>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
          <p className="text-xs text-center text-slate-400">
            By creating an account, you agree to our{' '}
            <Link
              to="/terms"
              onClick={closeLoginModal}
              className="underline"
              style={{ color: brand.colors.primary }}
            >
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              to="/privacy"
              onClick={closeLoginModal}
              className="underline"
              style={{ color: brand.colors.primary }}
            >
              Privacy Policy
            </Link>.
          </p>
        </div>
      </div>

      {/* Custom styles for Amplify Authenticator */}
      <style>{`
        .auth-container [data-amplify-authenticator] {
          --amplify-components-authenticator-router-border-width: 0;
          --amplify-components-authenticator-router-box-shadow: none;
          --amplify-components-authenticator-form-padding: 0;
          font-family: 'Nunito', system-ui, sans-serif;
        }

        .auth-container [data-amplify-authenticator] [data-amplify-router] {
          border: none;
          box-shadow: none;
          background: transparent;
          padding: 0;
        }

        .auth-container [data-amplify-authenticator] [data-amplify-form] {
          padding: 0;
        }

        .auth-container .amplify-tabs {
          border-bottom: 1px solid #e2e8f0;
          margin-bottom: 1.25rem;
          gap: 0;
        }

        .auth-container .amplify-tabs-item {
          font-weight: 600;
          font-size: 0.875rem;
          color: #64748b;
          border-bottom: 2px solid transparent;
          padding: 0.625rem 1rem;
          margin-bottom: -1px;
          transition: all 0.15s;
        }

        .auth-container .amplify-tabs-item[data-state="active"] {
          color: ${brand.colors.primaryHover};
          border-bottom-color: ${brand.colors.primaryHover};
          background: transparent;
        }

        .auth-container .amplify-tabs-item:hover:not([data-state="active"]) {
          color: ${brand.colors.primary};
          background: transparent;
        }

        .auth-container .amplify-input,
        .auth-container .amplify-field-group__control {
          border-radius: 0.5rem;
          border: 1px solid #e2e8f0;
          font-size: 0.875rem;
          padding: 0.625rem 0.875rem;
          transition: all 0.15s;
        }

        .auth-container .amplify-input:focus,
        .auth-container .amplify-field-group__control:focus {
          border-color: ${brand.colors.primary};
          box-shadow: 0 0 0 3px ${brand.colors.primary}26;
          outline: none;
        }

        .auth-container .amplify-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #334155;
          margin-bottom: 0.375rem;
        }

        .auth-container .amplify-button--primary {
          background: linear-gradient(to right, ${brand.colors.primaryHover}, ${brand.colors.secondary});
          border: none;
          font-weight: 600;
          padding: 0.75rem 1rem;
          font-size: 0.875rem;
          border-radius: 0.5rem;
          transition: all 0.2s;
          width: 100%;
        }

        .auth-container .amplify-button--primary:hover {
          background: linear-gradient(to right, ${brand.colors.primaryDark}, ${brand.colors.secondary});
          box-shadow: 0 4px 12px ${brand.colors.primaryHover}4D;
        }

        .auth-container .amplify-button--link {
          color: ${brand.colors.primaryHover};
          font-weight: 500;
          font-size: 0.875rem;
        }

        .auth-container .amplify-button--link:hover {
          color: ${brand.colors.primaryDark};
          text-decoration: none;
        }

        .auth-container .amplify-field-group__outer-end .amplify-button {
          border-radius: 0 0.5rem 0.5rem 0;
          border: 1px solid #e2e8f0;
          border-left: none;
        }

        .auth-container .amplify-alert {
          border-radius: 0.5rem;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }

        .auth-container .amplify-alert--error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
        }

        .auth-container .amplify-heading {
          display: none;
        }

        .auth-container .amplify-divider {
          margin: 1.25rem 0;
        }

        .auth-container .amplify-field {
          margin-bottom: 1rem;
        }

        .auth-container .amplify-button--small {
          font-size: 0.8125rem;
        }

        .auth-container [data-amplify-authenticator] [data-amplify-container] {
          width: 100%;
        }

        .auth-container .amplify-flex {
          gap: 0.5rem;
        }
      `}</style>
    </div>
  );
}
